<template>
  <div :class="['nav-bar', { 'nav-bar--sticky-top': isReadOnly }]">
    <div class="nav-bar__container">
      <div
        v-if="isFilterByItemAttributesEnabled || isMenuSearchEnabled"
        class="nav-bar__controls"
      >
        <button
          v-if="isMenuSearchEnabled"
          type="button"
          class="icon icon--link icon--link-gray far fa-search"
          title="Search Menu"
          @click="activateSearchHandler"
        />

        <button
          type="button"
          :class="['icon icon--link far fa-sliders-h', { 'icon--link-gray': filterAttributes.selected.length === 0 }]"
          title="Filter Menu"
          @click="$openModal('menuFilter')"
        />
      </div>

      <p
        v-else
        class="nav-bar__title"
      >
        Categories
      </p>

      <div class="nav-bar__nav">
        <button
          ref="categoriesNavBarScrollButton"
          :class="['nav-bar__button nav-bar__button--previous', { 'nav-bar__button--hide': isNavbarAtStart }]"
          type="button"
          tabindex="-1"
          @click="handleArrowClick('previous')"
        >
          <span class="icon fa-chevron-left" />
        </button>

        <button
          :class="['nav-bar__button nav-bar__button--next', { 'nav-bar__button--hide': isNavbarAtEnd }]"
          type="button"
          tabindex="-1"
          @click="handleArrowClick('next')"
        >
          <span class="icon fa-chevron-right" />
        </button>

        <div
          ref="categoriesNavBar"
          class="nav-bar__nav-items"
          @scroll="handleNavbarTrackPadScroll"
        >
          <button
            v-for="category in menu.filteredCategories"
            :id="`category-nav-button-${ category.categoryId}`"
            :key="category.categoryId"
            type="button"
            :class="['button button--rounded button--small normalcase regular button--primary', { 'button--light button--light-text': selectedCategoryId !== category.categoryId }]"
            tabindex="-1"
            @click="handleClickToScrollToCategory(category.categoryId)"
          >
            {{ category.displayName }}
          </button>
        </div>
      </div>

      <transition name="fade-in-slide-out-right">
        <div
          v-if="isSearchModeActive"
          class="nav-bar__search"
        >
          <search-input
            ref="searchInputRef"
            :model-value="modelValue"
            @update:model-value="handleSearchInputChange"
          />

          <button
            type="button"
            class="link primary"
            @click="deActivateSearchHandler"
          >
            Cancel
          </button>
        </div>
      </transition>
    </div>

    <div
      v-if="suggestedSearchTerms.length > 0 && isSearchModeActive && !modelValue"
      class="section section--alt-background padding-full"
    >
      <p>{{ menuSearchContent }}</p>

      <div class="flex justify-start gap">
        <button
          v-for="suggestedTerm in suggestedSearchTerms"
          :key="suggestedTerm.id"
          class="button button--primary button--small button--auto-width no-spacing"
          type="button"
          @click="setSuggestedTermHandler(suggestedTerm.name)"
        >
          {{ suggestedTerm.name }}
        </button>
      </div>
    </div>

    <menu-filter-modal />
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import MenuFilterModal from '@/components/globals/menu-filter-modal.vue';


  export default {
    name: 'MenuListFilter',

    components: {
      MenuFilterModal
    },

    props: {
      isReadOnly: {
        type: Boolean,
        default: false
      },

      isSearchModeActive: {
        type: Boolean,
        require: true
      },

      modelValue: {
        type: String,
        default: ''
      }
    },

    data() {
      return {
        selectedCategoryId: null,
        autoScrollingTimeout: null,
        isNavbarAtEnd: false,
        isNavbarAtStart: true,
        categoryNodes: [],
        isAutoScrolling: false,
        mainHeaderHeight: 70,
        navBarHeight: 46
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('menu', ['filterAttributes', 'menu']),
      ...mapGetters('merchant', ['merchant']),

      totalHeaderHeight() {
        let height = this.navBarHeight;

        if (!this.isReadOnly) {
          height += this.mainHeaderHeight;
        }
        return height;
      },

      isMenuSearchEnabled() {
        return this.merchant.features['enable-menu-search'];
      },

      menuSearchContent() {
        return this.content.MenuSearch?.metadata?.searchSuggestion;
      },

      isFilterByItemAttributesEnabled() {
        /** all available menu item attributes that are available for filtering */
        return this.filterAttributes.all.length > 0;
      },

      suggestedSearchTerms() {
        const searchSuggestions = this.filterAttributes.all.filter(attr => attr.isSearchSuggestion);

        if (searchSuggestions) {
          return searchSuggestions.map(suggestion => ({ id: suggestion.id, name: suggestion.name }));
        }

        return [];
      }
    },

    watch: {
      selectedCategoryId: 'onWatchSelectedCategoryId',
      'menu.filteredCategories': 'menuUpdatedHandler'
    },

    mounted() {
      this.onComponentMounted();
    },

    unmounted() {
      this.onComponentDestroyed();
    },

    methods: {
      onComponentMounted() {
        this.menuUpdatedHandler();

        window.addEventListener('resize', this.resizeEventHandler);
        window.addEventListener('scroll', this.scrollEventHandler);
      },

      onComponentDestroyed() {
        window.removeEventListener('resize', this.resizeEventHandler);
        window.removeEventListener('scroll', this.scrollEventHandler);
      },

      async activateSearchHandler() {
        const searchStatus = true;

        this.$emit('set-search-mode', searchStatus);

        await this.$nextTick();

        this.$refs.searchInputRef.focus();
      },

      deActivateSearchHandler() {
        const searchStatus = false;

        this.$emit('set-search-mode', searchStatus);
      },

      handleSearchInputChange(value) {
        this.$emit('update:modelValue', value);
      },

      setSuggestedTermHandler(suggestedSearchTerm) {
        this.$emit('update:modelValue', suggestedSearchTerm);
      },

      handleArrowClick(scrollDirection) {
        let distance = Math.ceil(this.$refs.categoriesNavBar.clientWidth * 0.6);

        if (scrollDirection === 'previous') {
          distance *= -1;
        }

        this.$refs.categoriesNavBar.scrollBy({ left: distance, behavior: 'smooth' });
      },

      async handleNavbarTrackPadScroll() {
        await this.$nextTick();

        const scroller = this.$refs.categoriesNavBar;

        if (!scroller) {
          this.isNavbarAtStart = true;
          this.isNavbarAtEnd = true;
          return;
        }

        this.isNavbarAtStart = scroller.scrollLeft <= 0;
        /** NOTE: The +2 pixels is necessary to fix weird behavior where the scroller doesn't scroll exactly to the end  */
        this.isNavbarAtEnd = scroller.scrollWidth - scroller.scrollLeft <= scroller.clientWidth + 2;
      },

      handleClickToScrollToCategory(categoryId) {
        clearTimeout(this.autoScrollingTimeout);

        if (this.selectedCategoryId !== categoryId) {
          this.isAutoScrolling = true;
        }

        this.selectedCategoryId = categoryId;
        const category = document.querySelector(`#category-${categoryId}`);
        const categoryRect = category.getBoundingClientRect();
        const categoryTop = window.pageYOffset + categoryRect.top - this.totalHeaderHeight;

        window.scrollTo({ top: categoryTop, left: 0, behavior: 'smooth' });

        this.autoScrollingTimeout = setTimeout(() => {
          this.isAutoScrolling = false;
        }, 1500);

        this.$emit('scroll-to-category', { categoryId, doNotClose: true });
      },

      onWatchSelectedCategoryId() {
        const scroller = this.$refs.categoriesNavBar;
        const activeCategoryNavButton = this.$refs.categoriesNavBar.querySelector(`#category-nav-button-${this.selectedCategoryId}`);
        const navScrollButton = this.$refs.categoriesNavBarScrollButton;

        if (!this.selectedCategoryId || !activeCategoryNavButton) {
          return;
        }

        const scrollDistance = activeCategoryNavButton.offsetLeft - navScrollButton.clientWidth;

        scroller.scrollTo({ left: scrollDistance, behavior: 'smooth' });
      },

      async menuUpdatedHandler() {
        await this.$nextTick();

        if (this.menu.filteredCategories.length > 0) {
          const matchingCategory = this.menu.filteredCategories.find(category => category.categoryId === this.selectedCategoryId);

          if (!matchingCategory) {
            this.selectedCategoryId = this.menu.filteredCategories[0].categoryId;
          }
        }

        this.categoryNodes = this.$el.querySelectorAll('.menu-list__category');
        this.scrollEventHandler();
        this.handleNavbarTrackPadScroll();
      },

      resizeEventHandler() {
        this.handleNavbarTrackPadScroll();
      },

      scrollEventHandler() {
        /* NOTE: This is not unit tested yet due to this being a bit experimental and likely to change */

        if (this.menu.filteredCategories.length === 0 || this.isAutoScrolling) {
          return;
        }

        const isAtBottomOfPage = (window.innerHeight + window.scrollY) >= document.body.scrollHeight;
        const isAtTopOfPage = window.scrollY <= 0;

        if (isAtTopOfPage) {
          this.selectedCategoryId = this.menu.filteredCategories[0].categoryId;
          return;
        }

        if (isAtBottomOfPage) {
          this.selectedCategoryId = this.menu.filteredCategories[this.menu.filteredCategories.length - 1].categoryId;
          return;
        }

        let activeNode;
        const referencePoint = window.pageYOffset + this.totalHeaderHeight + 160;

        for (let i = 0; i < this.categoryNodes.length; i++) {
          const rect = this.categoryNodes[i].getBoundingClientRect();
          const top = rect.top + window.pageYOffset;
          const bottom = rect.bottom + window.pageYOffset;

          if (top < referencePoint && bottom > referencePoint) {
            activeNode = this.categoryNodes[i];
          }
        }

        if (activeNode) {
          this.selectedCategoryId = Number(activeNode.getAttribute('id').replace('category-', ''));
        }

        else {
          this.selectedCategoryId = this.menu.filteredCategories[0].categoryId;
        }
      }
    }
  };
</script>
